import { motion } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { Link } from "react-router-dom";
import { useArticles } from "@/hooks/useArticles";
import { getDefaultThumbnail } from "@/lib/strapi";

const containerVariants = {
  hidden: {},
  visible: { transition: { staggerChildren: 0.12 } },
};

const itemVariants = {
  hidden: { opacity: 0, y: 24 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.5 } },
};

export function LatestNews() {
  const { articles, isLoading, error } = useArticles(3);
  const featured = articles[0];
  const rest = articles.slice(1);
  const fallbackThumbnail = getDefaultThumbnail();

  const handleImageError = (event) => {
    if (event.currentTarget.src !== fallbackThumbnail) {
      event.currentTarget.src = fallbackThumbnail;
    }
  };

  const getArticlePath = (article) => `/sports/${article?.slug || article?.id}`;

  return (
    <section
      data-testid="latest-news-section"
      className="bg-amber-pale py-24 md:py-32"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="flex items-end justify-between mb-12 md:mb-16">
          <div>
            <p className="font-inter font-light text-xs tracking-[0.25em] uppercase text-warm-gray mb-3">
              Latest Updates
            </p>
            <h2
              data-testid="news-section-title"
              className="font-inter font-semibold text-3xl md:text-4xl lg:text-5xl tracking-tighter text-near-black"
            >
              Latest Swimming Articles
            </h2>
          </div>
          <Link
            to="/sports"
            data-testid="news-view-all-link"
            className="hidden md:inline-flex items-center gap-1 font-inter font-medium text-sm text-amber-dusk hover:text-solar-orange transition-colors duration-150"
          >
            View all
            <ArrowUpRight size={14} />
          </Link>
        </div>

        <motion.div
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.2 }}
          className="grid grid-cols-1 md:grid-cols-12 gap-6"
        >
          {isLoading && (
            <div className="md:col-span-12 bg-white rounded-sm p-6 text-sm font-inter text-warm-gray">
              Loading latest articles...
            </div>
          )}

          {!!error && !isLoading && (
            <div className="md:col-span-12 bg-white rounded-sm p-6 text-sm font-inter text-red-600">
              Unable to load latest articles right now. Please try again shortly.
            </div>
          )}

          {!isLoading && !error && featured && (
          <motion.div variants={itemVariants} className="md:col-span-8 md:row-span-2">
            <Link
              to={getArticlePath(featured)}
              data-testid="news-featured-card"
              className="group relative overflow-hidden rounded-sm bg-near-black block h-full"
            >
              <img
                src={featured.image}
                alt={featured.title}
                loading="eager"
                fetchPriority="high"
                decoding="async"
                width={800}
                height={600}
                sizes="(max-width: 768px) 100vw, 66vw"
                onError={handleImageError}
                className="w-full h-full object-cover absolute inset-0 opacity-80 group-hover:scale-105 transition-transform duration-500"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-near-black via-near-black/50 to-transparent" />
              <div className="relative z-10 flex flex-col justify-end h-full min-h-[400px] md:min-h-[520px] p-8 md:p-10">
                <span className="inline-block bg-solar-orange text-near-black font-inter font-semibold text-[11px] tracking-[0.1em] uppercase px-3 py-1 rounded-sm mb-4 w-fit">
                  {featured.category}
                </span>
                <h3 className="font-inter font-semibold text-2xl md:text-3xl lg:text-4xl text-white tracking-tight leading-tight mb-3">
                  {featured.title}
                </h3>
                <p className="font-inter font-normal text-sm md:text-base text-white/70 max-w-xl leading-relaxed">
                  {featured.teaser}
                </p>
                <span className="inline-flex items-center gap-1 font-inter font-medium text-sm text-solar-orange mt-6 group-hover:gap-2 transition-all duration-150">
                  Read more
                  <ArrowUpRight size={14} />
                </span>
              </div>
            </Link>
          </motion.div>
          )}

          {!isLoading && !error && rest.map((article) => (
            <motion.div key={article.id} variants={itemVariants} className="md:col-span-4">
              <Link
                to={getArticlePath(article)}
                data-testid={`news-card-${article.id}`}
                className="group bg-white rounded-sm overflow-hidden border border-near-black/5 hover:-translate-y-1 transition-transform duration-200 block"
              >
                <div className="relative overflow-hidden aspect-[16/10] bg-warm-gray/10">
                  <img
                    src={article.image}
                    alt={article.title}
                    loading="lazy"
                    decoding="async"
                    width={800}
                    height={500}
                    sizes="(max-width: 768px) 100vw, (max-width: 1024px) 50vw, 33vw"
                    onError={handleImageError}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  />
                  <span className="absolute top-4 left-4 bg-solar-orange text-near-black font-inter font-semibold text-[11px] tracking-[0.1em] uppercase px-3 py-1 rounded-sm">
                    {article.category}
                  </span>
                </div>
                <div className="p-6">
                  <h3 className="font-inter font-semibold text-lg text-near-black tracking-tight leading-snug mb-2 group-hover:text-amber-dusk transition-colors duration-150">
                    {article.title}
                  </h3>
                  <p className="font-inter font-normal text-sm text-warm-gray leading-relaxed line-clamp-2">
                    {article.teaser}
                  </p>
                  <span className="inline-flex items-center gap-1 font-inter font-medium text-sm text-amber-dusk mt-4 group-hover:gap-2 transition-all duration-150">
                    Read more
                    <ArrowUpRight size={14} />
                  </span>
                </div>
              </Link>
            </motion.div>
          ))}
        </motion.div>

        <Link
          to="/sport-news"
          data-testid="news-view-all-mobile"
          className="md:hidden inline-flex items-center gap-1 font-inter font-medium text-sm text-amber-dusk mt-8"
        >
          View all articles
          <ArrowUpRight size={14} />
        </Link>
      </div>
    </section>
  );
}
